import { useParams } from 'react-router';
import { Helmet } from 'react-helmet-async';
import { useTeamBySlugQuery } from '@data/teams/useTeamBySlugQuery';
import { useArticlesQuery } from '@data/articles/useArticlesQuery';
import { useMatchesQuery } from '@data/matches/useMatchesQuery';
import { useLeagueTableQuery } from '@data/leagueTables/useLeagueTableQuery';
import { TeamOverview } from '@ui/club/components/TeamOverview';
import { useIsMobile } from '../../utils/useIsMobile';
import { MatchPeriodFilter, MatchSort, MatchStatusFilter, TagsMatchMode } from '@repo/types';
import { getCachedImage } from '@ui/utils/getCachedImage';
import { ImageVariant } from '@ui/common/components/Image/contracts';
import { NotFoundPage } from '../NotFoundPage';
import { Loader } from '@ui/common/components/Loader/Loader';

export function TeamPage() {
  const params = useParams();
  const isMobile = useIsMobile();

  const { data: team, isLoading: isTeamLoading, isError } = useTeamBySlugQuery(params.slug);

  const { data: articles, isLoading: areArticlesLoading } = useArticlesQuery({
    tags: params.slug,
    tagsMatchMode: TagsMatchMode.All,
    limit: 4,
  });

  const { data: fixtures, isLoading: areFixturesLoading } = useMatchesQuery(
    {
      teamId: team?.id,
      limit: 3,
      status: MatchStatusFilter.FIXTURE,
      period: MatchPeriodFilter.Days,
      periodLimit: 90,
      // @ts-ignore TODO; fix this type
      league: import.meta.env.VITE_FWP_LEAGUE,
    },
    {
      enabled: !!team?.id,
    }
  );

  const { data: results, isLoading: areResultsLoading } = useMatchesQuery(
    {
      teamId: team?.id,
      limit: 3,
      status: MatchStatusFilter.RESULT,
      sort: MatchSort.DESC,
      period: MatchPeriodFilter.Days,
      periodLimit: -30,
      // @ts-ignore TODO; fix this type
      league: import.meta.env.VITE_FWP_LEAGUE,
    },
    {
      enabled: !!team?.externalId,
    }
  );

  const { data: leagueTable, isLoading: isLeagueTableLoading } = useLeagueTableQuery(
    {
      teamId: team?.id,
    },
    {
      enabled: !!team?.externalId,
    }
  );

  if (isTeamLoading || areArticlesLoading || areFixturesLoading || areResultsLoading || isLeagueTableLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'calc(100vh - 100px)',
        }}
      >
        <Loader />
      </div>
    );
  }

  if (!params.slug || isError || !team) {
    return <NotFoundPage />;
  }

  const metaTitle = `${team.fullName} Team News, Fixtures and Results 2024/2025 | ${import.meta.env.VITE_SITE_NAME}`;
  const metaDescription = `For the latest news on ${
    team.fullName
  }, including scores, fixtures, team news, results, form guide and league position, visit the official website of the ${
    import.meta.env.VITE_SITE_NAME
  }.`;
  const image = getCachedImage(team.logo ?? '/site-logo.webp', ImageVariant.Detail);

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={image} />
        <meta property="og:image:alt" content={team.fullName} />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={image} />
      </Helmet>
      <TeamOverview
        isMobile={isMobile}
        team={team}
        articles={articles}
        leagueTable={leagueTable}
        results={results}
        fixtures={fixtures}
      />
    </>
  );
}
